import { Alert, Paper } from '@mui/material';
import {
  RichTreeView,
  TreeItemProps,
  TreeViewSelectionPropagation,
} from '@mui/x-tree-view';
import { TreeViewBaseItem } from '@mui/x-tree-view/models';
import {
  filter,
  flatMap,
  isEmpty,
  includes,
  uniq,
  map,
  every,
  some,
  reduce,
  flatten,
  compact,
  toLower,
  intersection,
  difference,
} from 'lodash';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { DebouncedTextField, FormControls } from 'components';

import { AccordionFormikTreeItem, BasicFormikTreeItem } from './FormikTreeItem';

export type FormikTreeNode = TreeViewBaseItem<{
  id: string;
  label: string;
  path: string[];
}>;

export type FormikTreeAdditionalItemSettingsComponentProps = {
  item: TreeItemProps;
  index: number;
  disabled: boolean;
};

export type FormikTreeProps = {
  fieldName: string;
  fieldValueIdPropertyName?: string;
  items: FormikTreeNode[];
  defaultSelectedItems?: string[];
  onChange: (
    event: React.SyntheticEvent | null,
    selectedItemIds: string[],
  ) => void;
  renderExtraLabelContent?: (item: TreeItemProps) => ReactNode;
  additionalItemSettings?: {
    component: React.FC<FormikTreeAdditionalItemSettingsComponentProps>;
    defaultValues: Record<string, any>;
  };
  enableSearch?: boolean;
  leafIds: string[];
  collapseRoots?: boolean;
};

export const FormikTree: React.FC<FormikTreeProps> = ({
  items,
  defaultSelectedItems = [],
  onChange,
  fieldValueIdPropertyName = 'id',
  fieldName,
  renderExtraLabelContent,
  additionalItemSettings,
  enableSearch = true,
  leafIds,
  collapseRoots = true,
}) => {
  const { t: tCommon } = useTranslation();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] =
    useState<string[]>(defaultSelectedItems);

  const isInitialRender = useRef(true);

  useEffect(() => {
    isInitialRender.current = false;
  }, []);

  const [selectionPropagation] = useState<TreeViewSelectionPropagation>({
    parents: true,
    descendants: true,
  });

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value.trim());
  }, []);

  const treeSearchMap = useMemo((): Record<string, FormikTreeNode> => {
    if (!enableSearch) return {};

    const registerTreeNode = (
      acc: Record<string, FormikTreeNode>,
      node: FormikTreeNode,
    ) => {
      acc[node.id] = { ...node, label: toLower(node.label) };
      map(node.children, (child) => registerTreeNode(acc, child));
      return acc;
    };

    return reduce(items, registerTreeNode, {});
  }, [items, enableSearch]);

  const { visibleItems, expandedItemsIds } = useMemo(() => {
    if (!searchQuery) {
      return { visibleItems: items, expandedItemsIds: [] };
    }

    const searchTerms = compact(searchQuery.toLowerCase().split(/\s+/));

    const matchingLeafPaths = Object.values(treeSearchMap)
      .filter(
        (node) =>
          isEmpty(node.children) &&
          every(searchTerms, (term: string) =>
            some(node.path, (nodeId: string) =>
              includes(treeSearchMap[nodeId]?.label, term),
            ),
          ),
      )
      .map((leafNode) => leafNode.path);

    if (matchingLeafPaths.length === 0) {
      return { visibleItems: [], expandedItemsIds: [] };
    }

    const visibleItemsIds = uniq(flatten(matchingLeafPaths));
    const filterVisibleNodes = (items: FormikTreeNode[]): FormikTreeNode[] =>
      compact(
        map(items, (node) => {
          if (includes(visibleItemsIds, node.id)) {
            return {
              ...node,
              children: filterVisibleNodes(node.children!),
            };
          }
        }),
      );

    return {
      visibleItems: filterVisibleNodes(items),
      expandedItemsIds: visibleItemsIds,
    };
  }, [searchQuery, items, treeSearchMap]);

  const findItemByIds = useCallback(
    (items: FormikTreeNode[], itemIds: string[]): FormikTreeNode[] =>
      isEmpty(items)
        ? []
        : [
            ...filter(items, (item) => includes(itemIds, item.id)),
            ...findItemByIds(flatMap(items, 'children'), itemIds),
          ],
    [],
  );

  const expandedItems = useMemo(() => {
    if (searchQuery) {
      return expandedItemsIds;
    }

    const expandedIds = uniq(
      flatMap(findItemByIds(items, selectedItems), 'path'),
    );

    if (isInitialRender.current && collapseRoots) {
      const roots = map(items, (root) => root.path[0]);
      return difference(expandedIds, roots);
    }

    return expandedIds;
  }, [
    searchQuery,
    collapseRoots,
    findItemByIds,
    items,
    selectedItems,
    expandedItemsIds,
  ]);

  const handleSelectedItemsChange = useCallback(
    (e: React.SyntheticEvent | null, selectedItemIds: string[]) => {
      const selectedLeafIds = intersection(leafIds, selectedItemIds);
      onChange(e, selectedLeafIds);
      // It is necessary to save the current selection of nodes
      // for correct tree re-render when the search value changes
      if (enableSearch) {
        setSelectedItems(selectedLeafIds);
      }
    },
    [enableSearch, onChange, leafIds],
  );

  const renderItem = useCallback(
    (item: TreeItemProps) => {
      if (isEmpty(item.children) && additionalItemSettings?.component) {
        return (
          <AccordionFormikTreeItem
            fieldName={fieldName}
            fieldValueIdPropertyName={fieldValueIdPropertyName}
            item={item}
            renderExtraLabelContent={renderExtraLabelContent}
            additionalItemSettings={additionalItemSettings}
          />
        );
      }

      return (
        <BasicFormikTreeItem
          item={item}
          renderExtraLabelContent={renderExtraLabelContent}
        />
      );
    },
    [
      additionalItemSettings,
      renderExtraLabelContent,
      fieldName,
      fieldValueIdPropertyName,
    ],
  );

  return (
    <Paper className="tw-p-2">
      <FormControls>
        {enableSearch && (
          <DebouncedTextField
            fullWidth
            placeholder={tCommon('components.list_filter.label')}
            onChangeHandler={handleSearchChange}
            size="small"
            slotProps={{
              input: {
                sx: { borderRadius: 1 },
              },
            }}
          />
        )}
        {isEmpty(visibleItems) ? (
          <Alert severity="info">
            {tCommon('components.data_wrapper.state.empty')}
          </Alert>
        ) : (
          <RichTreeView
            key={searchQuery}
            checkboxSelection
            multiSelect
            selectionPropagation={selectionPropagation}
            items={visibleItems}
            defaultSelectedItems={selectedItems}
            defaultExpandedItems={expandedItems}
            slots={{
              item: renderItem,
            }}
            onSelectedItemsChange={handleSelectedItemsChange}
          />
        )}
      </FormControls>
    </Paper>
  );
};
